import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccessTime as ClockIcon,
  CameraAlt as CameraIcon,
  AssignmentTurnedIn as RequestIcon,
  LocationOn as LocationIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  clearEmployeeClockSession,
  EMPLOYEE_CLOCK_TOKEN_KEY,
  getEmployeeClockHeaders,
} from "./employeeClockStorage";
import { useTenantContext } from "../../context/TenantContext";

const API_URL = process.env.REACT_APP_API_URL1;
const MAX_PHOTO_EDGE = 1024;
const PHOTO_QUALITY = 0.78;

const toLocalTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-GB", { timeZone: "Asia/Yangon" });
};

const toDateInputValue = (date = new Date()) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toApiDateTime = (value) => {
  if (!value) return null;
  return value.length === 16 ? `${value.replace("T", " ")}:00` : value.replace("T", " ");
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function EmployeeClockPage() {
  const navigate = useNavigate();
  const { tenantProfile } = useTenantContext();
  const clockVideoRef = useRef(null);
  const manualVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [activeTab, setActiveTab] = useState("clock");
  const [cameraMode, setCameraMode] = useState(null);
  const [status, setStatus] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [manualPhoto, setManualPhoto] = useState(null);
  const [manualPhotoPreview, setManualPhotoPreview] = useState("");
  const [manualForm, setManualForm] = useState({
    request_date: toDateInputValue(),
    requested_clock_in_time: "",
    requested_clock_out_time: "",
    reason: "",
  });
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [message, setMessage] = useState(null);

  const token = useMemo(() => localStorage.getItem(EMPLOYEE_CLOCK_TOKEN_KEY) || "", []);

  const loadStatus = useCallback(async () => {
    if (!localStorage.getItem(EMPLOYEE_CLOCK_TOKEN_KEY)) {
      navigate("/web-clock/login", { replace: true });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/web-clock/status`, {
        headers: getEmployeeClockHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to load status");
      setStatus(data);
    } catch (err) {
      if (String(err.message || "").includes("Session")) {
        clearEmployeeClockSession();
        navigate("/web-clock/login", { replace: true });
      } else {
        setMessage({ severity: "error", text: err.message || "Failed to load status" });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, token]);

  useEffect(() => {
    if (!location || !status?.allowedLocations?.length) {
      setSelectedLocation(null);
      return;
    }
    const match = status.allowedLocations.find((loc) => {
      const distance = calculateDistance(
        Number(location.latitude),
        Number(location.longitude),
        Number(loc.latitude),
        Number(loc.longitude)
      );
      return distance <= Number(loc.radius_meters || 100);
    });
    setSelectedLocation(match || null);
  }, [location, status]);

  const requestLocation = async (required) => {
    if (!navigator.geolocation) {
      if (required) throw new Error("Browser location is not supported on this device.");
      return null;
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setLocation(coords);
          resolve(coords);
        },
        (err) => {
          if (required) reject(new Error(err.message || "Unable to get browser location"));
          else resolve(null);
        },
        { enableHighAccuracy: true, timeout: required ? 15000 : 7000, maximumAge: 30000 }
      );
    });
  };

  const handleGetLocation = async (required) => {
    setMessage(null);
    try {
      await requestLocation(required);
    } catch (err) {
      setMessage({
        severity: "error",
        text: err.message || "Unable to get browser location.",
      });
    }
  };

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setCameraMode(null);
    setCameraReady(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const startCamera = async (mode) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Camera is not supported by this browser.");
    }

    stopCamera();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: "user",
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });

    cameraStreamRef.current = stream;
    setCameraReady(false);
    setCameraMode(mode);
  };

  useEffect(() => {
    if (!cameraMode || !cameraStreamRef.current) return;

    const video = cameraMode === "manual" ? manualVideoRef.current : clockVideoRef.current;
    if (!video) return;

    video.srcObject = cameraStreamRef.current;
    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        setMessage({
          severity: "warning",
          text: "Camera opened, but the browser blocked preview playback. Please tap Open Camera again.",
        });
      });
    }
  }, [cameraMode]);

  const handleStartCamera = async (mode) => {
    setMessage(null);
    try {
      await startCamera(mode);
    } catch (err) {
      setMessage({
        severity: "error",
        text: err.message || "Unable to open camera. Please allow camera access and use HTTPS.",
      });
    }
  };

  const captureLivePhoto = async (mode) => {
    const video = mode === "manual" ? manualVideoRef.current : clockVideoRef.current;
    if (!video) {
      throw new Error("Camera is not ready yet.");
    }

    if (!video.videoWidth || !video.videoHeight) {
      await new Promise((resolve) => {
        const timeout = window.setTimeout(resolve, 800);
        const done = () => {
          window.clearTimeout(timeout);
          resolve();
        };
        video.addEventListener("loadedmetadata", done, { once: true });
        video.addEventListener("loadeddata", done, { once: true });
      });
    }

    if (!video.videoWidth || !video.videoHeight || video.readyState < 2) {
      setCameraReady(false);
      throw new Error("Camera is not ready yet.");
    }

    const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", PHOTO_QUALITY));
    if (!blob) throw new Error("Unable to capture photo.");

    const file = new File([blob], `${mode}_live_photo_${Date.now()}.jpg`, { type: "image/jpeg" });
    const preview = URL.createObjectURL(file);

    if (mode === "manual") {
      setManualPhoto(file);
      setManualPhotoPreview(preview);
    } else {
      setPhoto(file);
      setPhotoPreview(preview);
    }
    stopCamera();
  };

  const handleCaptureLivePhoto = async (mode) => {
    setMessage(null);
    try {
      await captureLivePhoto(mode);
    } catch (err) {
      setMessage({
        severity: "warning",
        text: err.message || "Camera is not ready yet. Please wait a moment and try again.",
      });
    }
  };

  useEffect(() => {
    stopCamera();
  }, [activeTab, stopCamera]);

  const uploadPhoto = async (documentType, file = photo) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", documentType);
    const res = await fetch(`${API_URL}/web-clock/photo`, {
      method: "POST",
      headers: getEmployeeClockHeaders(),
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || "Photo upload failed");
    return data;
  };

  const submitClock = async () => {
    if (!status?.staff) return;
    setActionLoading(true);
    setMessage(null);
    try {
      const isClockOut = status.isActive;
      if (status.staff.is_photo_required && !photo) {
        throw new Error(isClockOut ? "Clock-out photo is required" : "Clock-in photo is required");
      }

      const coords = await requestLocation(status.staff.web_geofence_required);
      let boundary = selectedLocation;
      if (status.staff.web_geofence_required) {
        const source = coords || location;
        boundary = status.allowedLocations.find((loc) => {
          const distance = calculateDistance(
            Number(source.latitude),
            Number(source.longitude),
            Number(loc.latitude),
            Number(loc.longitude)
          );
          return distance <= Number(loc.radius_meters || 100);
        });
        if (!boundary) throw new Error("You are outside assigned web clock geo-access boundaries.");
      }

      const uploadedPhoto = await uploadPhoto(isClockOut ? "CLOCK_OUT_PHOTO" : "CLOCK_IN_PHOTO");
      const endpoint = isClockOut ? "clock-out" : "clock-in";
      const prefix = isClockOut ? "clock_out" : "clock_in";
      const activeCoords = coords || location;
      const payload = {
        attendance_id: isClockOut ? status.session?.id : undefined,
        [`${prefix}_latitude`]: activeCoords?.latitude,
        [`${prefix}_longitude`]: activeCoords?.longitude,
        [`${prefix}_accuracy`]: activeCoords?.accuracy,
        [`${prefix}_location_id`]: boundary?.id || null,
        [`${prefix}_location_name`]: boundary?.location_name || null,
        [`${prefix}_document_id`]: uploadedPhoto?.dms_id || null,
        client_key: window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()),
        notes,
      };

      const res = await fetch(`${API_URL}/web-clock/${endpoint}`, {
        method: "POST",
        headers: {
          ...getEmployeeClockHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Clock action failed");

      setMessage({ severity: "success", text: data.message || "Clock action completed" });
      setPhoto(null);
      setPhotoPreview("");
      setNotes("");
      stopCamera();
      await loadStatus();
    } catch (err) {
      setMessage({ severity: "error", text: err.message || "Clock action failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const submitManualRequest = async () => {
    if (!status?.staff?.staff_id) return;
    setActionLoading(true);
    setMessage(null);
    try {
      if (!manualForm.request_date) throw new Error("Request date is required");
      if (!manualForm.requested_clock_in_time && !manualForm.requested_clock_out_time) {
        throw new Error("Please enter requested clock-in or clock-out time");
      }
      if (!manualPhoto) {
        throw new Error("Supporting live photo is required");
      }
      if (manualForm.requested_clock_in_time && manualForm.request_date !== manualForm.requested_clock_in_time.substring(0, 10)) {
        throw new Error("Clock-in time must be on the request date");
      }
      if (manualForm.requested_clock_out_time && manualForm.request_date !== manualForm.requested_clock_out_time.substring(0, 10)) {
        throw new Error("Clock-out time must be on the request date");
      }

      const uploadedAttachment = await uploadPhoto("MANUAL_ATTENDANCE_ATTACHMENT", manualPhoto);
      const res = await fetch(`${API_URL}/manual-attendance/submit`, {
        method: "POST",
        headers: {
          ...getEmployeeClockHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          staff_id: status.staff.staff_id,
          request_date: manualForm.request_date,
          requested_clock_in_time: toApiDateTime(manualForm.requested_clock_in_time),
          requested_clock_out_time: toApiDateTime(manualForm.requested_clock_out_time),
          reason: manualForm.reason,
          attachment_path: uploadedAttachment?.file_url || uploadedAttachment?.dms_id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Manual request submission failed");

      setMessage({ severity: "success", text: "Manual attendance request submitted for approval" });
      setManualForm({
        request_date: toDateInputValue(),
        requested_clock_in_time: "",
        requested_clock_out_time: "",
        reason: "",
      });
      setManualPhoto(null);
      setManualPhotoPreview("");
      stopCamera();
    } catch (err) {
      setMessage({ severity: "error", text: err.message || "Manual request submission failed" });
    } finally {
      setActionLoading(false);
    }
  };

  const logout = () => {
    clearEmployeeClockSession();
    navigate("/web-clock/login", { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress />
      </Box>
    );
  }

  const isActive = status?.isActive;
  const staff = status?.staff || {};
  const isClockPhotoReady = !staff.is_photo_required || Boolean(photo);
  const isClockLocationReady = !staff.web_geofence_required || Boolean(location && selectedLocation);
  const clockRequirementMessages = [
    staff.is_photo_required && !photo ? "Capture live photo" : null,
    staff.web_geofence_required && !location ? "Get browser location" : null,
    staff.web_geofence_required && location && !selectedLocation ? "Move inside assigned geo-access boundary" : null,
  ].filter(Boolean);
  const isClockActionDisabled = actionLoading || cameraMode === "clock" || !isClockPhotoReady || !isClockLocationReady;
  const isManualRequestReady = Boolean(
    manualForm.request_date &&
    (manualForm.requested_clock_in_time || manualForm.requested_clock_out_time) &&
    manualForm.reason.trim() &&
    manualPhoto
  );
  const isManualActionDisabled = actionLoading || cameraMode === "manual" || !isManualRequestReady;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: { xs: 2, md: 3 } }}>
      <Paper sx={{ maxWidth: 720, mx: "auto", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Box sx={{ p: 3, bgcolor: "#fff", borderBottom: "1px solid #e2e8f0" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 0 }}>
              {tenantProfile?.logo_url && (
                <Box
                  component="img"
                  src={tenantProfile.logo_url}
                  alt={`${tenantProfile?.tenant_name || "PeopleHub"} logo`}
                  sx={{ width: 46, height: 46, objectFit: "contain", flexShrink: 0 }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a" }}>
                  {tenantProfile?.tenant_name || "PeopleHub"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                  PeopleHub Employee Clock | {staff.full_name} {staff.staff_code ? `(${staff.staff_code})` : ""}
                </Typography>
              </Box>
            </Box>
            <Button startIcon={<LogoutIcon />} color="inherit" onClick={logout}>Logout</Button>
          </Stack>
        </Box>

        <Stack spacing={3} sx={{ p: 3 }}>
          {message && <Alert severity={message.severity}>{message.text}</Alert>}

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{ borderBottom: "1px solid #e2e8f0" }}
          >
            <Tab value="clock" icon={<ClockIcon />} iconPosition="start" label="Clock" />
            <Tab value="manual" icon={<RequestIcon />} iconPosition="start" label="Manual Request" />
          </Tabs>

          {activeTab === "clock" && (
            <>
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: isActive ? "#ecfdf5" : "#eff6ff", border: "1px solid #dbeafe" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <ClockIcon color={isActive ? "success" : "primary"} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{isActive ? "Clocked In" : "Ready to Clock In"}</Typography>
                  <Chip size="small" label="Web Clock" color={isActive ? "success" : "primary"} />
                </Stack>
                {isActive && (
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Clock-in time: {toLocalTime(status.session?.clock_in_time)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: "#475569", mt: 0.5 }}>
                  Photo: {staff.is_photo_required ? "Required" : "Optional"} | Web geofence: {staff.web_geofence_required ? "Required" : "Not required"}
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStatus} disabled={actionLoading}>Refresh</Button>
                <Button
                  variant={staff.web_geofence_required && !isClockLocationReady ? "contained" : "outlined"}
                  color={staff.web_geofence_required && !isClockLocationReady ? "primary" : "primary"}
                  startIcon={<LocationIcon />}
                  onClick={() => handleGetLocation(staff.web_geofence_required)}
                  disabled={actionLoading}
                >
                  Get Location
                </Button>
              </Stack>

              {location && (
                <Alert severity={selectedLocation ? "success" : staff.web_geofence_required ? "warning" : "info"}>
                  Browser location accuracy: {Math.round(location.accuracy || 0)}m
                  {selectedLocation ? ` | Matched: ${selectedLocation.location_name}` : ""}
                </Alert>
              )}

              {clockRequirementMessages.length > 0 && (
                <Alert severity="warning">
                  Required before {isActive ? "clock-out" : "clock-in"}: {clockRequirementMessages.join(", ")}.
                </Alert>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                  {isActive ? "Clock-Out Live Photo" : "Clock-In Live Photo"} {staff.is_photo_required ? "(Required)" : "(Optional)"}
                </Typography>
                <Stack spacing={2}>
                  {cameraMode === "clock" && (
                    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0", bgcolor: "#020617" }}>
                      <Box
                        component="video"
                        ref={clockVideoRef}
                        playsInline
                        muted
                        autoPlay
                        onLoadedMetadata={() => setCameraReady(true)}
                        onCanPlay={() => setCameraReady(true)}
                        onLoadedData={() => setCameraReady(true)}
                        onPlaying={() => setCameraReady(true)}
                        sx={{ width: "100%", maxHeight: 360, objectFit: "cover", display: "block" }}
                      />
                    </Box>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                    {cameraMode === "clock" ? (
                      <>
                        <Button variant="contained" startIcon={<CameraIcon />} onClick={() => handleCaptureLivePhoto("clock")} disabled={actionLoading || !cameraReady}>
                          {cameraReady ? "Capture Photo" : "Preparing Camera..."}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={stopCamera} disabled={actionLoading}>
                          Cancel Camera
                        </Button>
                      </>
                    ) : (
                      <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => handleStartCamera("clock")} disabled={actionLoading}>
                        {photo ? "Retake Live Photo" : "Open Camera"}
                      </Button>
                    )}
                    {photoPreview && <Box component="img" src={photoPreview} alt="Clock selfie preview" sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 2, border: "1px solid #e2e8f0" }} />}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Photo must be captured live from this browser.
                  </Typography>
                </Stack>
              </Box>

              <TextField
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />

              <Button
                variant="contained"
                size="large"
                color={isActive ? "error" : "success"}
                onClick={submitClock}
                disabled={isClockActionDisabled}
                sx={{ py: 1.4, fontWeight: 900 }}
              >
                {actionLoading ? "Processing..." : isActive ? "Clock Out" : "Clock In"}
              </Button>
            </>
          )}

          {activeTab === "manual" && (
            <>
              <Alert severity="info">
                Submit this when you forgot clock-in or clock-out. It will follow the existing manager and HR approval workflow.
              </Alert>

              <TextField
                label="Request Date"
                type="date"
                value={manualForm.request_date}
                onChange={(e) => setManualForm((prev) => ({ ...prev, request_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Requested Clock-In"
                  type="datetime-local"
                  value={manualForm.requested_clock_in_time}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, requested_clock_in_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Requested Clock-Out"
                  type="datetime-local"
                  value={manualForm.requested_clock_out_time}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, requested_clock_out_time: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Reason"
                value={manualForm.reason}
                onChange={(e) => setManualForm((prev) => ({ ...prev, reason: e.target.value }))}
                multiline
                rows={3}
                fullWidth
                required
              />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                  Supporting Live Photo (Required)
                </Typography>
                <Stack spacing={2}>
                  {cameraMode === "manual" && (
                    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0", bgcolor: "#020617" }}>
                      <Box
                        component="video"
                        ref={manualVideoRef}
                        playsInline
                        muted
                        autoPlay
                        onLoadedMetadata={() => setCameraReady(true)}
                        onCanPlay={() => setCameraReady(true)}
                        onLoadedData={() => setCameraReady(true)}
                        onPlaying={() => setCameraReady(true)}
                        sx={{ width: "100%", maxHeight: 360, objectFit: "cover", display: "block" }}
                      />
                    </Box>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                    {cameraMode === "manual" ? (
                      <>
                        <Button variant="contained" startIcon={<CameraIcon />} onClick={() => handleCaptureLivePhoto("manual")} disabled={actionLoading || !cameraReady}>
                          {cameraReady ? "Capture Photo" : "Preparing Camera..."}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={stopCamera} disabled={actionLoading}>
                          Cancel Camera
                        </Button>
                      </>
                    ) : (
                      <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => handleStartCamera("manual")} disabled={actionLoading}>
                        {manualPhoto ? "Retake Live Photo" : "Open Camera"}
                      </Button>
                    )}
                    {manualPhotoPreview && <Box component="img" src={manualPhotoPreview} alt="Manual attendance attachment preview" sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 2, border: "1px solid #e2e8f0" }} />}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Upload from gallery is not available; capture a live photo before submitting.
                  </Typography>
                </Stack>
              </Box>

              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={submitManualRequest}
                disabled={isManualActionDisabled}
                sx={{ py: 1.4, fontWeight: 900 }}
              >
                {actionLoading ? "Submitting..." : "Submit Manual Request"}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
